import React, { Suspense, lazy, useEffect, useRef } from "react";
import { Dismiss16Regular } from "@fluentui/react-icons";
import { useWebAgentContext } from "./WebAgentContext";
import { useWebChatStyles } from "../WebChat/WebChatStyles";
import { useWebAgentTelemetry } from "./useWebAgentTelemetry";

const WebAgent = lazy(() => import("./WebAgent"));

export const AgentLoadingPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const styles = useWebChatStyles();
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []);
  return (
    <div
      className={styles.chatWindow}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dining-agent-title"
      data-testid="webagent-window"
    >
      <div className={styles.header}>
        <span id="dining-agent-title" className={styles.headerTitle}>
          Dining Agent
        </span>
        <button
          ref={closeButtonRef}
          className={styles.headerCloseButton}
          onClick={onClose}
          aria-label="Close chat"
        >
          <Dismiss16Regular />
        </button>
      </div>
      <div className={styles.stateContainer} role="status" aria-live="polite">
        <p>Loading Dining Agent…</p>
      </div>
    </div>
  );
};

export const LazyWebAgent: React.FC = () => {
  const styles = useWebChatStyles();
  const { isOpen, setIsOpen, openerRef } = useWebAgentContext();
  const telemetry = useWebAgentTelemetry();
  const previousOpenRef = useRef(false);

  useEffect(() => {
    const wasOpen = previousOpenRef.current;
    previousOpenRef.current = isOpen;

    if (!wasOpen && isOpen) {
      telemetry.opened();
    } else if (wasOpen && !isOpen) {
      telemetry.closed();
      const saved = openerRef.current;
      openerRef.current = null;
      try {
        if (saved && document.contains(saved)) {
          saved.focus();
        } else {
          const launcher = document.querySelector<HTMLElement>('[aria-label="Open Dining Agent"]');
          launcher?.focus?.();
        }
      } catch {
        /* noop */
      }
    }
  }, [isOpen, telemetry, openerRef]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (
        typeof window.matchMedia === "function" &&
        window.matchMedia("(max-width: 499px)").matches
      ) {
        return;
      }
      setIsOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, setIsOpen]);

  if (!isOpen) return null;
  return (
    <>
      <div
        className={styles.backdrop}
        aria-hidden="true"
        data-testid="webagent-backdrop"
        onClick={() => setIsOpen(false)}
      />
      <Suspense fallback={<AgentLoadingPanel onClose={() => setIsOpen(false)} />}>
        <WebAgent />
      </Suspense>
    </>
  );
};

export default LazyWebAgent;
